import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { truckService } from '../api/api';
import { useAuth } from '../auth/AuthContext';
import { formatPortalDate } from '../utils/dateFormat';

const today = () => new Date().toISOString().slice(0, 10);

function formatDate(value) {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? value : formatPortalDate(parsed);
}

export default function TruckWashes({ truckId, truck }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [needsWashing, setNeedsWashing] = useState(false);
  const [washDate, setWashDate] = useState(today());
  const [washedBy, setWashedBy] = useState('');
  const [washLocation, setWashLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const latest = rows[0] || null;
  const currentlyNeedsWash = latest?.needs_washing === true;
  const currentUserName = (user?.name || user?.email || '').trim();

  const load = useCallback(async () => {
    if (!truckId) return;
    try {
      setLoading(true);
      setError('');
      const response = await truckService.getWashes(truckId);
      const nextRows = Array.isArray(response.data) ? response.data : [];
      setRows(nextRows);
      setNeedsWashing(nextRows[0]?.needs_washing === true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load vehicle wash records.');
    } finally {
      setLoading(false);
    }
  }, [truckId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!washedBy.trim() && currentUserName) setWashedBy(currentUserName);
  }, [currentUserName, washedBy]);

  const canSave = useMemo(() => {
    if (saving) return false;
    if (needsWashing) return true;
    return Boolean(washDate && washedBy.trim());
  }, [needsWashing, saving, washDate, washedBy]);

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await truckService.createWash(truckId, {
        needs_washing: needsWashing,
        wash_date: needsWashing ? null : washDate,
        washed_by: needsWashing ? null : washedBy.trim(),
        wash_location: needsWashing ? null : washLocation.trim(),
        notes: notes.trim(),
      });
      setSuccess(needsWashing ? 'Wash reminder created for this vehicle.' : 'Vehicle wash recorded and reminder completed.');
      if (!needsWashing) {
        setWashDate(today());
        setWashedBy(currentUserName);
        setWashLocation('');
      }
      setNotes('');
      await load();
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save vehicle wash record.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Box>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <LocalCarWashIcon sx={{ color: '#123b83' }} />
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#001f3f' }}>
                  Vehicle Wash Tracking
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {truck?.name || 'This vehicle'} can be marked as needing washing, then closed when washed.
              </Typography>
            </Box>
            <Chip
              color={currentlyNeedsWash ? 'warning' : 'success'}
              label={currentlyNeedsWash ? 'Needs Washing' : 'Wash Status OK'}
              sx={{ fontWeight: 900 }}
            />
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
          {currentlyNeedsWash && !needsWashing && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This vehicle has an active wash reminder. Recording the completed wash below will close that reminder automatically.
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={needsWashing} onChange={(event) => setNeedsWashing(event.target.checked)} />}
                label={<Typography sx={{ fontWeight: 900 }}>Vehicle needs washing</Typography>}
              />
              <Typography variant="body2" color="text.secondary">
                Checking this creates an active vehicle reminder associated with this vehicle.
              </Typography>
            </Grid>

            {!needsWashing && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date Washed"
                    value={washDate}
                    onChange={(event) => setWashDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Washed By"
                    value={washedBy}
                    onChange={(event) => setWashedBy(event.target.value)}
                    helperText={currentUserName ? 'Prefilled from your signed-in account.' : ''}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Where"
                    value={washLocation}
                    onChange={(event) => setWashLocation(event.target.value)}
                    placeholder="Yard, wash bay, vendor, etc."
                    size="small"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                multiline
                minRows={2}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <EventAvailableIcon />}
                disabled={!canSave}
                onClick={save}
                sx={{ fontWeight: 900, borderRadius: 2 }}
              >
                {saving ? 'Saving…' : needsWashing ? 'Create Wash Reminder' : currentlyNeedsWash ? 'Record Wash & Complete Reminder' : 'Record Vehicle Wash'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>Wash History</Typography>
        </Box>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">No wash records yet.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Date Washed</TableCell>
                  <TableCell>Washed By</TableCell>
                  <TableCell>Where</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.needs_washing ? 'warning' : 'success'}
                        label={row.needs_washing ? 'Needs Washing' : 'Washed'}
                        sx={{ fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(row.wash_date)}</TableCell>
                    <TableCell>{row.washed_by || '—'}</TableCell>
                    <TableCell>{row.wash_location || '—'}</TableCell>
                    <TableCell>{row.notes || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
}
